import {
  CartItem as CartItemType,
  CouponItemInterface,
  StoreInterface,
  WhatsappCheckoutOption,
} from "../../../assets/interfaces";
import symbols from "../../../assets/js/utils/currency-symbols";
import { genarateStringFromVariantValues, shuffleArray } from "../../../assets/js/utils/functions";
import { getItemThumbnail, resolvePhone, sendFacebookEvent } from "../../../assets/js/utils/utils";
import CartContext from "../../../contexts/cart-context";
import { AppBtn } from "../../ui/buttons";
import ErrorLabel from "../../ui/error-label";
import { ModalBody, ModalFooter } from "../../ui/modal";
import CartItem from "./cart-item";
import { useRequest } from "@/api/utils";
import { CompleteSelectionParams } from "@/api/interfaces/whatsapp.interface";
import { CompleteSelection } from "@/api/whatsapp";
import { useMemo } from "react";
import { CheckoutForm } from "../modals/checkout";
import CartMainItem from "./cart-main-item";
import Portal from "@/components/portal";
import { ApplyCouponModal } from "./apply-coupon-modal";
import { FormikProps } from "formik";
import { ModalsState } from "@/components/hooks/useModals";
import CartContextV2 from "@/contexts/cart-context-v2";

interface Props {
  store: StoreInterface;
  nextStep: VoidFunction;
  changeStep: (step: string) => void;
  couponModal: { modals: ModalsState<"coupon">; toggleModal: (key: string) => void };
  applyCoupon: (paymentCoupon: CouponItemInterface) => void;
  coupon: { couponCode: string; discountAmount: number };
  removeCoupon: () => void;
  form: FormikProps<CheckoutForm>;
}

export type GroupedCart = {
  [key: string]: CartItemType[];
};

export const CartStep: React.FC<Props> = ({
  store,
  nextStep,
  changeStep,
  form,
  coupon,
  couponModal,
  applyCoupon,
  removeCoupon,
}) => {
  const {
    cart,
    addOrRemoveItem,
    updateItemQuantity,
    setQuantity,
    price,
    cartLink,
    cartError,
    setCartError,
    removeItem,
    formatAsCurrency,
    currencies,
    priceUnformatted,
    minimumOrderQuantitiesReached,
  } = CartContextV2.useContainer();

  const checkoutOptions = shuffleArray<WhatsappCheckoutOption>(store?.checkout_channels?.whatsapp);

  const getEnquiryLink = (prefix: string, phone: string) => {
    return encodeURI(
      `https://api.whatsapp.com/send/?phone=${resolvePhone(phone)}&text=${prefix}\n\n${cart
        .map((c) => (c.is_deleted || c.is_unavailable ? "" : `📦 ${getFullProductName(c)} - ${c.quantity}x\n`))
        .join("")}\n\n💰 Total price: *${price}*\n\nCheck out cart here:\n${cartLink}`
    );
  };

  const proceedToBuy = () => {
    if (cart.some((c) => c.is_deleted || c.is_unavailable)) {
      setCartError("Please remove unavailable items from cart");
      return;
    }

    sendFacebookEvent("InitiateCheckout", {
      content_ids: cart.map((c) => c.item.id),
      num_items: cart.length,
      value: price,
      currency: currencies.active,
      contents: cart.map((c) => ({ id: c.item.id, quantity: c.quantity, item_price: c.item.price })),
    });
    nextStep();
  };

  const enquire = () => {
    // window.open(
    //   getEnquiryLink("Hi, i'll like to make an enquiry about these items:", resolvePhone(checkoutOptions[0].phone))
    // );
    // return;
    changeStep("enquiry");
  };

  const groupedCart = cart.reduce((acc: GroupedCart, item: CartItemType) => {
    const itemId = item.item.id;

    if (!acc[itemId]) {
      acc[itemId] = [];
    }

    acc[itemId].push(item);
    return acc;
  }, {});

  const groupedCartArray = Object.values(groupedCart);
  return (
    <>
      <ModalBody>
        {coupon?.couponCode && (
          <div className="bg-white sticky top-0 z-[1000] -mx-5 sm:-mx-6.25 transform -translate-y-5 sm:-translate-y-6.25 shadow-card !mb-0">
            <div className="py-2.5 text-black-muted bg-green-500 bg-opacity-20 px-4 text-1xs sm:text-sm flex items-start sm:items-center justify-center">
              {/* prettier-ignore */}
              <svg  width="20px" viewBox="0 0 23 22" fill="none" className="transition-all ease-out duration-200 text-primary-500 mr-1 flex-shrink-0">
                <path d="M20.2358 9.96414L18.8425 8.57081C18.6041 8.33247 18.4116 7.86497 18.4116 7.53497V5.55497C18.4116 4.74831 17.7516 4.08831 16.945 4.08831H14.9741C14.6441 4.08831 14.1766 3.89581 13.9383 3.65747L12.545 2.26414C11.9766 1.69581 11.0416 1.69581 10.4733 2.26414L9.06164 3.65747C8.83247 3.89581 8.36497 4.08831 8.02581 4.08831H6.05497C5.24831 4.08831 4.58831 4.74831 4.58831 5.55497V7.52581C4.58831 7.85581 4.39581 8.32331 4.15747 8.56164L2.76414 9.95497C2.19581 10.5233 2.19581 11.4583 2.76414 12.0266L4.15747 13.42C4.39581 13.6583 4.58831 14.1258 4.58831 14.4558V16.4266C4.58831 17.2333 5.24831 17.8933 6.05497 17.8933H8.02581C8.35581 17.8933 8.82331 18.0858 9.06164 18.3241L10.455 19.7175C11.0233 20.2858 11.9583 20.2858 12.5266 19.7175L13.92 18.3241C14.1583 18.0858 14.6258 17.8933 14.9558 17.8933H16.9266C17.7333 17.8933 18.3933 17.2333 18.3933 16.4266V14.4558C18.3933 14.1258 18.5858 13.6583 18.8241 13.42L20.2175 12.0266C20.8133 11.4675 20.8133 10.5325 20.2358 9.96414ZM7.83331 8.24997C7.83331 7.74581 8.24581 7.33331 8.74997 7.33331C9.25414 7.33331 9.66664 7.74581 9.66664 8.24997C9.66664 8.75414 9.26331 9.16664 8.74997 9.16664C8.24581 9.16664 7.83331 8.75414 7.83331 8.24997ZM9.23581 14.2358C9.09831 14.3733 8.92414 14.4375 8.74997 14.4375C8.57581 14.4375 8.40164 14.3733 8.26414 14.2358C7.99831 13.97 7.99831 13.53 8.26414 13.2641L13.7641 7.76414C14.03 7.49831 14.47 7.49831 14.7358 7.76414C15.0016 8.02997 15.0016 8.46997 14.7358 8.73581L9.23581 14.2358ZM14.25 14.6666C13.7366 14.6666 13.3241 14.2541 13.3241 13.75C13.3241 13.2458 13.7366 12.8333 14.2408 12.8333C14.745 12.8333 15.1575 13.2458 15.1575 13.75C15.1575 14.2541 14.7541 14.6666 14.25 14.6666Z" fill="currentColor"/>
                </svg>
              <div className="flex items-center flex-wrap">
                <div className="mr-3">
                  <b className="font-semibold">{formatAsCurrency(Math.abs(coupon?.discountAmount))}</b> saved with your
                  coupon applied
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="flex flex-col h-auto overflow-y-auto">
          <p className="text-black-muted text-1sm md:text-1sm mb-2.5">
            Review your items and proceed to checkout when you&apos;re ready
          </p>
          <div className="-mb-5">
            <ErrorLabel error={cartError} perm={true} />
            {cart.length > 0 && !coupon && (
              <button
                onClick={() => couponModal.toggleModal("coupon")}
                className="text-black-secondary flex text-1xs sm:text-sm font-semibold underline mb-8"
              >
                Enter a Coupon Code
              </button>
            )}
          </div>
          <div>
            {groupedCartArray.map((items, index) =>
              items.length > 0 && items[0].variant ? (
                <CartMainItem items={items} key={index}>
                  {items.map((item, itemIndex) => (
                    <CartItem
                      key={itemIndex}
                      {...{
                        item,
                        updateItemQuantity,
                        addOrRemoveItem,
                        setItemQuantity: setQuantity,
                        removeItem,
                        formatAsCurrency,
                      }}
                    />
                  ))}
                </CartMainItem>
              ) : (
                <CartItem
                  key={index}
                  {...{
                    item: items[0],
                    updateItemQuantity,
                    addOrRemoveItem,
                    setItemQuantity: setQuantity,
                    removeItem,
                    formatAsCurrency,
                  }}
                />
              )
            )}
          </div>
          {false &&
            cart.map((item, index) => (
              <CartItem
                key={index}
                {...{
                  item,
                  updateItemQuantity,
                  addOrRemoveItem,
                  setItemQuantity: setQuantity,
                  removeItem,
                  formatAsCurrency,
                }}
              />
            ))}
          {/* </div> */}
          {cart.length > 0 && false && (
            <div className="flex items-center justify-between text-sm py-2.5 px-3 bg-grey-fields-100 rounded-8">
              <div className="flex items-center gap-1.25 text-black-placeholder">
                <figure className="h-6 w-6 rounded-full bg-accent-yellow-500 text-white flex items-center justify-center">
                  <span className="font-medium text-sm">{symbols[currencies.active]}</span>
                </figure>

                <span className="text-black-muted text-1xs sm:text-sm font-medium">Total price:</span>
              </div>
              <span className="text-primary-500 text-1xs sm:text-sm font-semibold">{price}</span>
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter className="flex-col !py-0 md:pt-0 !px-0">
        <div className="w-full border-b border-grey-divider flex items-center justify-between py-2.5 px-5 sm:px-6.25">
          <div className="flex items-center gap-2">
            <span className="text-black-placeholder text-1sm font-medium">Subtotal</span>
            {cart.length > 0 && (
              <div
                className={`flex items-center text-1xs text-black-placeholder gap-1 pl-1 ${
                  cart.length > 1 ? "pr-2.5" : "pr-1"
                } py-1 border border-grey-border rounded-full`}
              >
                <div className="flex -space-x-3 rtl:space-x-reverse">
                  {cart.slice(0, 3).map((i, index) => (
                    <img
                      key={index}
                      className="w-7.5 h-7.5 border-2 border-white rounded-full object-cover"
                      src={getItemThumbnail(i?.item)}
                      alt=""
                    />
                  ))}
                </div>
                {cart.length > 3 && <span>+{cart.length - 3}</span>}
              </div>
            )}
          </div>
          <span className="text-black text-sm sm:text-base font-semibold font-display">{price}</span>
        </div>
        <div className="w-full py-3.75 px-5 sm:px-6.25">
          <div className="flex items-center gap-x-3">
            <AppBtn className="flex-1" color="neutral" onClick={() => enquire()} size="lg">
              Make an enquiry
            </AppBtn>
            <AppBtn
              className="flex-1"
              onClick={proceedToBuy}
              disabled={cart.length < 1 || !minimumOrderQuantitiesReached}
              size="lg"
            >
              Proceed
            </AppBtn>
          </div>
        </div>
      </ModalFooter>

      <Portal>
        <ApplyCouponModal
          toggle={() => couponModal.toggleModal("coupon")}
          applyCoupon={applyCoupon}
          store={store.id}
          show={couponModal.modals.coupon}
          itemsAmount={priceUnformatted}
          currency={currencies.active}
          customerId={form?.values?.customer_id}
        />
      </Portal>
    </>
  );
};

export default CartStep;

export const getFullProductName = (c: CartItemType) => {
  let name = "";

  if (c?.variant) {
    if (c?.item?.variants.type === "custom") {
      name = `${c.item.name} (${genarateStringFromVariantValues(c?.variant?.values)})`;
    } else {
      const hasExtraOption = c.item?.variants?.options[0]?.values !== undefined;
      name = `${c.item.name} (Option ${c.item.variants.options.findIndex((o) => o.id === c.variant.id) + 1}${
        hasExtraOption ? ` - ${Object.values(c?.variant?.values)[0]}` : ""
      })`;
    }
  } else {
    name = c.item.name;
  }
  return name.replace(/&/g, "and");
};
